import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Twitter, MessageCircle, Phone, Mail } from "lucide-react";
import { SITE, NAV_ALL, SOCIALS, LEGAL, CONTACT } from "../../config/site";

const SOCIAL_ICONS = {
  LinkedIn: Linkedin,
  Instagram: Instagram,
  Facebook: Facebook,
  "X (Twitter)": Twitter,
  WhatsApp: MessageCircle,
};

export default function Footer() {
  return (
    <footer className="bg-navy text-white" data-testid="site-footer">
      <div className="container-x py-14 sm:py-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <Link to="/" aria-label="The Vantage Institute — home" data-testid="footer-logo" className="inline-block">
              <img
                src="/images/vantage-institute-logo.webp"
                alt="The Vantage Institute"
                width="120"
                height="120"
                loading="lazy"
                className="h-24 w-24 shrink-0 object-contain sm:h-28 sm:w-28"
                data-testid="footer-logo-image"
              />
            </Link>
            <p className="mt-4 font-sub text-xs font-semibold uppercase tracking-[0.18em] text-gold">
              {SITE.positioning}
            </p>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-platinum">
              {SITE.mission}
            </p>
            <p className="mt-6 text-sm text-white/90">{SITE.group}</p>
            <p className="mt-1 text-sm text-white/90">{SITE.division}</p>
            <ul className="mt-6 flex items-center gap-3" aria-label="Social channels">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.label];
                return (
                  <li key={s.label}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`The Vantage Institute on ${s.label}`}
                      data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                      className="flex min-h-[44px] min-w-[44px] items-center justify-center border border-white/20 text-platinum transition-colors hover:border-gold hover:text-gold"
                    >
                      <Icon size={18} aria-hidden="true" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
          <nav aria-label="Footer">
            <p className="eyebrow-light">Navigate</p>
            <ul className="mt-4 space-y-2.5">
              {NAV_ALL.filter((n) => ["Learn", "Thinking Toolbox™", "For Organisations", "Advisory (Vantage Group™)", "Knowledge Hub", "About", "Founder", "Contact", "Programmes", "Events"].includes(n.label)).map((n) => (
                <li key={n.label}>
                  <Link to={n.to} className="text-sm text-platinum transition-colors hover:text-gold" data-testid={`footer-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div>
            <p className="eyebrow-light">Talk to Vantage</p>
            <p className="mt-4 text-sm leading-relaxed text-platinum">
              For programmes, corporate capability partnerships or general enquiries, start a conversation with the Institute.
            </p>
            <div className="mt-4 flex items-center gap-3" aria-label="Direct contact">
              <a
                href={CONTACT.phoneHref}
                aria-label="Call The Vantage Institute"
                title="Call The Vantage Institute"
                data-testid="footer-phone"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center border border-white/20 text-platinum transition-colors hover:border-gold hover:text-gold"
              >
                <Phone size={18} aria-hidden="true" />
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                aria-label="Email The Vantage Institute"
                title="Email The Vantage Institute"
                data-testid="footer-email"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center border border-white/20 text-platinum transition-colors hover:border-gold hover:text-gold"
              >
                <Mail size={18} aria-hidden="true" />
              </a>
            </div>
            <Link to="/contact" className="btn-gold mt-5 !min-h-[44px] !px-5 !py-2 text-xs" data-testid="footer-cta-contact">
              Talk to Vantage
            </Link>
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-platinum/80">
            © {new Date().getFullYear()} The Vantage Institute™. All rights reserved.
          </p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {LEGAL.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-xs text-platinum/80 transition-colors hover:text-gold" data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
